import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, AlertTriangle,
  History, RotateCcw, Image as ImageIcon, Upload, Check, Pencil
} from 'lucide-react';
import { contentService } from '../../services/ContentService';

const FIELD_LABELS = {
  title: 'Titulo',
  subtitle: 'Subtitulo',
  cta: 'Call to Action (CTA)',
  handle: 'Handle (@marca)',
};

const FIELD_PLACEHOLDERS = {
  title: 'Frase principal do post',
  subtitle: 'Descritivo curto',
  cta: 'Acao desejada (deixe vazio se nao usar)',
  handle: '@suamarca',
};

const ReactRenderContentEditor = () => {
  const { batchId, contentId } = useParams();
  const isMountedRef = useRef(true);
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [content, setContent] = useState(null);
  const [editableFields, setEditableFields] = useState({});
  const [originalFields, setOriginalFields] = useState({});
  const [brandImageUrl, setBrandImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [needsBackground, setNeedsBackground] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const loadContentData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await contentService.getBatch(batchId);
      const batchData = response.data;
      const contentData = batchData.contents?.find(c => c.id === contentId);

      if (!contentData) throw new Error('Conteudo nao encontrado');
      if (contentData.model !== 'react_render') throw new Error('Edicao disponivel apenas para react_render');
      if (contentData.status !== 'completed') throw new Error('Conteudo ainda nao foi processado');

      const meta = contentData.metadata || {};
      const fields = meta.editable_fields || {};

      // Garante todos os 4 campos
      const normalized = {
        title: fields.title || '',
        subtitle: fields.subtitle || '',
        cta: fields.cta || '',
        handle: fields.handle || '',
      };

      setContent(contentData);
      setEditableFields(normalized);
      setOriginalFields(JSON.parse(JSON.stringify(normalized)));
      setBrandImageUrl(meta.background_image_url || '');
      setOriginalImageUrl(meta.background_image_url || '');
      setNeedsBackground(!!meta.needs_background_image);
      setEditHistory(contentData.edit_history || []);
    } catch (err) {
      if (isMountedRef.current) setError(err.message || 'Erro ao carregar');
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [batchId, contentId]);

  useEffect(() => { loadContentData(); }, [loadContentData]);

  const hasChanges = (
    JSON.stringify(editableFields) !== JSON.stringify(originalFields) ||
    brandImageUrl !== originalImageUrl
  );

  const handleFieldChange = (key, value) => {
    setEditableFields(prev => ({ ...prev, [key]: value }));
  };

  const handleImageUploadFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await contentService.uploadEditImage(contentId, file);
      if (result?.url) {
        setBrandImageUrl(result.url);
      } else {
        alert('Falha no upload');
      }
    } catch (err) {
      alert('Erro no upload: ' + (err.message || ''));
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await contentService.saveReactRenderEdit(batchId, contentId, {
        editable_fields: editableFields,
        brand_image_url: brandImageUrl || null,
        regenerate: true,
      });
      if (!isMountedRef.current) return;
      setOriginalFields(JSON.parse(JSON.stringify(editableFields)));
      setOriginalImageUrl(brandImageUrl);
      setSaveSuccess(true);
      await loadContentData();
      setTimeout(() => isMountedRef.current && setSaveSuccess(false), 3000);
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.detail || err.message || 'Tente novamente'));
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const handleRevert = async (historyIndex) => {
    if (!window.confirm('Reverter para esta versao?')) return;
    setIsSaving(true);
    try {
      await contentService.restoreContentVersion(batchId, contentId, historyIndex);
      if (!isMountedRef.current) return;
      await loadContentData();
      setShowHistory(false);
    } catch (err) {
      alert('Erro ao reverter: ' + (err.message || ''));
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
          <p className="text-gray-500">Carregando editor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Nao foi possivel abrir o editor</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <Link to={`/contents/batch/${batchId}`} className="inline-block px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800">
            Voltar
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/contents/batch/${batchId}`} className="p-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-500" />
                Editor React Render
              </h1>
              <p className="text-sm text-gray-500">
                {content?.metadata?.layout_agent_name || content?.metadata?.layout_type || 'Layout'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editHistory.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium"
              >
                <History className="w-4 h-4" />
                Historico ({editHistory.length})
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="flex items-center gap-2 px-5 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> :
                saveSuccess ? <Check className="w-4 h-4" /> :
                <Save className="w-4 h-4" />}
              {isSaving ? 'Salvando...' : saveSuccess ? 'Salvo!' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Preview */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Preview</h2>
          {content?.result_url ? (
            <img
              src={content.result_url}
              alt={content.metadata?.layout_agent_name}
              className="w-full rounded-xl border border-gray-100"
            />
          ) : (
            <div className="aspect-square bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
              Sem preview
            </div>
          )}
          <p className="text-xs text-gray-500 mt-3">
            Salve para regerar o post com os novos valores.
          </p>
        </div>

        {/* Form de edicao */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-1">Textos editaveis</h2>
            <p className="text-xs text-gray-500">
              O layout e visual sao mantidos. So o conteudo textual e a imagem sao editaveis.
            </p>
          </div>

          {Object.keys(FIELD_LABELS).map(field => (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {FIELD_LABELS[field]}
              </label>
              {field === 'subtitle' || field === 'cta' ? (
                <textarea
                  rows={2}
                  value={editableFields[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  placeholder={FIELD_PLACEHOLDERS[field]}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              ) : (
                <input
                  type="text"
                  value={editableFields[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  placeholder={FIELD_PLACEHOLDERS[field]}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              )}
            </div>
          ))}

          {needsBackground && (
            <div className="border-t border-gray-100 pt-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imagem de fundo (AI ou upload)
              </label>
              {brandImageUrl && (
                <img
                  src={brandImageUrl}
                  alt="Background atual"
                  className="w-full max-h-48 object-cover rounded-xl border border-gray-100 mb-3"
                />
              )}
              <input
                type="text"
                value={brandImageUrl || ''}
                onChange={(e) => setBrandImageUrl(e.target.value)}
                placeholder="Cole uma URL https://..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono mb-2"
              />
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUploadFile}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium"
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {isUploading ? 'Subindo...' : 'Upload imagem'}
                </button>
                {brandImageUrl && (
                  <button
                    onClick={() => setBrandImageUrl('')}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Remover imagem
                  </button>
                )}
              </div>
            </div>
          )}

          {!needsBackground && (
            <div className="border-t border-gray-100 pt-4 text-xs text-gray-400 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Este layout nao usa imagem de fundo. Apenas textos sao editaveis.
            </div>
          )}
        </div>
      </div>

      {/* History panel */}
      {showHistory && editHistory.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-20 flex items-center justify-center p-6" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Historico de versoes</h3>
            <ul className="space-y-2">
              {editHistory.map((h, i) => (
                <li key={i} className="border border-gray-200 rounded-xl p-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500">{h.edited_at?.substring(0, 19) || `versao #${i+1}`}</div>
                    {h.previous_url && (
                      <a href={h.previous_url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-blue-500 hover:underline truncate block">
                        {h.previous_url.substring(0, 50)}...
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => handleRevert(i)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-xs"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reverter
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactRenderContentEditor;
